import {SupportLink} from "@/components/formSubtext/subComponents/SupportLink/SupportLink.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import classNames from "classnames";
import {isValidElement, ReactNode} from "react";
import {FiAlertCircle} from "react-icons/fi";
import styles from "./FormSubtext.module.scss";

export type FormSubtextVariant = "default" | "checkbox";

export const FORM_SUBTEXT_SUPPORT_LINK_POSITIONS = ["before", "after"] as const;
export type FormSubtextSupportLinkPosition = (typeof FORM_SUBTEXT_SUPPORT_LINK_POSITIONS)[number];

interface FormSubtextProps {
  subText?: string | ReactNode;
  error?: string;
  supportLink?: string;
  supportLinkText?: string;
  className?: string;
  alwaysShowSubText?: boolean;
  variant?: FormSubtextVariant;
  supportLinkPosition?: FormSubtextSupportLinkPosition;
  warning?: string;
}

/**
 * Checks if a value contains HTML code.
 */
function containsHTML(value: string) {
  return value.indexOf("</a>") !== -1 || value.indexOf("</strong>") !== -1 || value.indexOf("</em>") !== -1;
}

// it is intended and expected that error text would overwrite the subtext until resolved.
export const FormSubtext = ({
  subText,
  error,
  supportLink,
  supportLinkText,
  alwaysShowSubText,
  className,
  variant = "default",
  supportLinkPosition = "after",
  warning,
}: FormSubtextProps) => {
  if (!error && !subText && !supportLink) {
    return null;
  }

  const supportLinkElement = supportLink && <SupportLink href={supportLink} text={supportLinkText} />;

  return (
    <div className={classNames(styles.formSubtext, variant === "checkbox" && styles.checkbox, className)}>
      {error && (
        <div className="d-flex align-items-center gap-1 pulse-color-red-500">
          <Icon icon={FiAlertCircle} removeDivDimensions />
          {!isValidElement(error) && containsHTML(error) ? (
            <span className={classNames({"me-1": supportLink && subText})} dangerouslySetInnerHTML={{__html: error}} />
          ) : (
            <span className={classNames({"me-1": supportLink && subText})}>{error}</span>
          )}
        </div>
      )}
      {supportLinkPosition === "before" && supportLinkElement}
      {(!error || alwaysShowSubText) && (
        <>
          {subText && !isValidElement(subText) && containsHTML(subText.toString()) ? (
            <span
              className={classNames({"me-1": supportLink && subText}, styles.formSubtextText)}
              dangerouslySetInnerHTML={{__html: subText.toString()}}
            />
          ) : (
            <>
              <span className={classNames({"me-1": supportLink && subText}, styles.formSubtextText)}>
                {
                  // If subText is a string, ensure it ends with a period
                  typeof subText === "string" ? subText.replace(/([^.])$/, "$1.") : subText
                }
              </span>
              {warning && <span className={"pulseTextBodySmall pulse-color-red-600"}>{warning}</span>}
            </>
          )}
        </>
      )}
      {supportLinkPosition === "after" && supportLinkElement}
    </div>
  );
};
